/**
 * The words of the 7-day reverse trial (#4703; founder approval of the pricing
 * and trial strategy, 2026-10-18; mock A of #4668). One definition, read by
 * every trial surface, so the promise, the wall and the money-back terms
 * cannot drift apart.
 *
 * The rules these words keep (research_notes/Stratia pricing and trial
 * strategy/minors_consumer_law.md):
 *  - no meter, no count, no countdown: the trial names its real end DATE,
 *    never "3 days left" and never a ticking clock;
 *  - fair use, never "unlimited";
 *  - the refund's terms (30 days, at most one college read, how to ask) sit
 *    beside every mention of it (16 CFR 239.3; ADR 0055);
 *  - nothing is lost: the wall says what the student keeps, never what they
 *    would lose (FTC 2022 dark patterns; ADR 0013 "a lapsed Pass is not a
 *    deletion").
 */
import { seasonPassPriceUsd } from '../constants/seasonPricing';
import { REFUND_POLICY_LINE } from '../constants/seasonPassPlan';
import { SUPPORT_EMAIL } from './planRefusal';

export const TRIAL_DAYS = 7;

const DATE = { weekday: 'short', month: 'short', day: 'numeric' };

/** "Sat, Oct 25", or null when the date is unreadable (absence, never a guess). */
export function trialEndDate(trial) {
    const d = trial?.ends_at ? new Date(trial.ends_at) : null;
    return d && !Number.isNaN(d.getTime()) ? d.toLocaleDateString('en-US', DATE) : null;
}

/** The one-time terms: what the trial is, stated once, in plain words. */
export const TRIAL_TERMS = 'Full access for 7 days, subject to fair use. No card, nothing to cancel, and everything you make stays yours.';

export function trialLine(trial) {
    const end = trialEndDate(trial);
    return end
        ? `You have everything in Stratia through ${end}.`
        : 'You have everything in Stratia for 7 days.';
}

// The strategy's line was "money back within 30 days"; the founder narrowed
// the refund an hour later (ADR 0055, #4718: a full read on no more than one
// college), so the offer names the refund only with its condition, through
// the one REFUND_POLICY_LINE every surface shares (16 CFR 239).
// `priceLabel`: the price this visitor is shown (#4966: "₹9,999" in India
// behind its flag); the season clock's dollars otherwise.
export const keepGoingLine = (priceLabel = `$${seasonPassPriceUsd()}`) =>
    `Keep going for the whole season: ${priceLabel}. ${REFUND_POLICY_LINE}.`;

/** How to ask for the refund, beside every mention of it. */
export const moneyBackTerms = () =>
    `${REFUND_POLICY_LINE}: write to ${SUPPORT_EMAIL}.`;

/** The trial's internal ceiling, reached: said plainly, never a figure. */
export const TRIAL_CAP_HEADLINE = "You've used this week's trial allowance.";
export const TRIAL_CAP_LINE = 'The Season Pass continues it for the whole season. Everything you made so far stays yours.';

/** Day 8: what the student keeps, then the one offer. */
export const WALL_HEADLINE = 'Everything you made is still here';
export const WALL_KEEPS = 'Your list, every read you made and your profile stay yours, and your odds at every college stay free. New reads, essay feedback and plans come with the Season Pass.';
